import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LoginRespuesta, UsuarioSesion } from '@tms/contratos';
import { CLAVE_SESION, alNoAutorizado, api } from '../api';

interface Guardada {
  token: string;
  expiraEn: string;
  usuario: UsuarioSesion;
}

interface SesionCtx {
  usuario: UsuarioSesion | null;
  login: (email: string, contrasena: string) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<SesionCtx | null>(null);

function leer(): Guardada | null {
  try {
    const bruto = localStorage.getItem(CLAVE_SESION);
    if (!bruto) return null;
    const s = JSON.parse(bruto) as Guardada;
    return s.token && new Date(s.expiraEn).getTime() > Date.now() ? s : null;
  } catch {
    return null;
  }
}

export function SesionProvider({ children }: { children: ReactNode }) {
  const [guardada, setGuardada] = useState<Guardada | null>(leer);

  const logout = useCallback(() => {
    localStorage.removeItem(CLAVE_SESION);
    setGuardada(null);
  }, []);

  // Un 401 de la API cierra la sesión: RequireAuth redirige al login.
  useEffect(() => {
    alNoAutorizado(logout);
  }, [logout]);

  const login = useCallback(async (email: string, contrasena: string) => {
    const r: LoginRespuesta = await api.login({ email, contrasena });
    const s: Guardada = { token: r.token, expiraEn: r.expiraEn, usuario: r.usuario };
    localStorage.setItem(CLAVE_SESION, JSON.stringify(s));
    setGuardada(s);
  }, []);

  const valor = useMemo(() => ({ usuario: guardada?.usuario ?? null, login, logout }), [guardada, login, logout]);
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSesion(): SesionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSesion fuera de SesionProvider');
  return c;
}
